import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  LockKeyhole,
  Shield,
  Lock,
  Users,
  TrendingUp,
  Zap,
  CheckCircle,
  Star,
  Briefcase,
  Globe,
  Server,
  Key,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  comingSoon?: boolean;
}

export function InfraVaultHub() {
  const [selectedTier, setSelectedTier] = useState<string>('premium');

  const pricingTiers: PricingTier[] = [
    {
      name: 'Basic Vault',
      price: '$499',
      description: 'Perfect for individual traders and small operations',
      features: [
        'Up to $100K secured storage',
        'Basic encryption (AES-256)',
        'Single user access',
        '24/7 monitoring',
        'Mobile app access',
        'Email support'
      ]
    },
    {
      name: 'Premium Vault',
      price: '$1,499',
      description: 'Advanced security for professional traders',
      features: [
        'Up to $1M secured storage',
        'Military-grade encryption',
        'Multi-user access (up to 5)',
        'Real-time audit logs',
        'Hardware wallet integration',
        'API access',
        'Priority support 24/7',
        'Insurance coverage included'
      ],
      highlighted: true
    },
    {
      name: 'Enterprise Vault',
      price: 'Custom',
      description: 'Maximum security for institutions and brokers',
      features: [
        'Unlimited secured storage',
        'Quantum-resistant encryption',
        'Unlimited multi-user access',
        'Custom compliance controls',
        'Dedicated infrastructure',
        'White-label solutions',
        'SLA guarantee 99.99%',
        'Dedicated account manager',
        'On-premise deployment option'
      ]
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Military-Grade Security',
      description: 'Multi-layer encryption with quantum-resistant algorithms protecting your assets.',
      color: 'violet'
    },
    {
      icon: Lock,
      title: 'Decentralized Infrastructure',
      description: 'Your keys, your crypto. Full custody control with distributed architecture.',
      color: 'purple'
    },
    {
      icon: Users,
      title: 'Multi-Signature Access',
      description: 'Enterprise-grade access control with customizable approval workflows.',
      color: 'violet'
    },
    {
      icon: Server,
      title: 'High Availability',
      description: '99.99% uptime guarantee with redundant systems across multiple regions.',
      color: 'purple'
    },
    {
      icon: Key,
      title: 'Hardware Security Modules',
      description: 'Keys stored in certified HSM devices meeting FIPS 140-2 Level 3 standards.',
      color: 'violet'
    },
    {
      icon: Globe,
      title: 'Global Compliance',
      description: 'Regulatory compliant across 50+ jurisdictions with automated reporting.',
      color: 'purple'
    }
  ];

  const useCases = [
    {
      icon: TrendingUp,
      title: 'Professional Traders',
      description: 'Secure your trading capital with instant access when opportunities arise'
    },
    {
      icon: Briefcase,
      title: 'Crypto Brokers',
      description: 'Manage client funds with full transparency and audit trails'
    },
    {
      icon: Users,
      title: 'Paymasters',
      description: 'Handle high-volume transactions with enterprise-grade security'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-3xl">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-700 to-indigo-900">
          <motion.div
            className="absolute inset-0 bg-gradient-to-tr from-violet-500/30 via-transparent to-purple-500/30"
            animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Animated particles */}
          <div className="absolute inset-0">
            {[...Array(15)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white/20 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 px-8 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 mb-6">
              <LockKeyhole className="w-5 h-5 text-violet-200" />
              <span className="text-sm font-semibold text-white">INFRA TECHNOLOGY Group</span>
            </div>

            <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-white via-violet-100 to-purple-100 bg-clip-text text-transparent">
              INFRA VAULT
            </h1>

            <p className="text-2xl text-white/90 mb-6 max-w-3xl mx-auto">
              Enterprise-Grade Decentralized Vaults
            </p>

            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Secure private vaults designed for paymasters, traders, and brokers. 
              Partner of Nardium, powered by INFRA TECHNOLOGY.
            </p>

            <div className="flex gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-violet-700 hover:bg-violet-50"
              >
                <Shield className="mr-2 h-5 w-5" />
                Explore Plans
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                disabled
              >
                <ExternalLink className="mr-2 h-5 w-5" />
                Access Platform (Coming Soon)
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Partnership Badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-center gap-3">
              <Star className="w-6 h-6 text-violet-600 dark:text-violet-400" />
              <p className="text-lg font-semibold text-violet-900 dark:text-violet-100">
                Official Partner of Nardium DeFi Platform
              </p>
              <Star className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Use Cases */}
      <div>
        <h2 className="text-3xl font-bold mb-6 text-center">Who We Serve</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {useCases.map((useCase, index) => {
            const Icon = useCase.icon;
            return (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-violet-200 dark:border-violet-800 hover:shadow-xl hover:shadow-violet-500/20 transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2 text-violet-900 dark:text-violet-100">
                      {useCase.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400">
                      {useCase.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Pricing Tiers */}
      <div>
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-4">Choose Your Vault Plan</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Select the perfect security tier for your needs. All plans include our core security features.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={tier.highlighted ? 'md:scale-105' : ''}
            >
              <Card 
                className={`h-full relative overflow-hidden cursor-pointer transition-all ${
                  tier.highlighted 
                    ? 'border-violet-500 dark:border-violet-400 shadow-2xl shadow-violet-500/30' 
                    : 'border-violet-200 dark:border-violet-800 hover:border-violet-400'
                }`}
                onClick={() => setSelectedTier(tier.name)}
              >
                {tier.highlighted && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-1 rounded-bl-lg">
                    <span className="text-sm font-semibold">RECOMMENDED</span>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="text-2xl text-violet-900 dark:text-violet-100">
                    {tier.name}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {tier.description}
                  </CardDescription>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-violet-600 dark:text-violet-400">
                      {tier.price}
                    </span>
                    {tier.price !== 'Custom' && (
                      <span className="text-slate-600 dark:text-slate-400 ml-2">/month</span>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`w-full ${
                      tier.highlighted 
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700' 
                        : 'bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-300'
                    }`}
                    size="lg"
                    disabled
                  >
                    {tier.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Features Grid */}
      <div>
        <h2 className="text-3xl font-bold mb-6 text-center">Core Security Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-violet-200 dark:border-violet-800 hover:shadow-lg hover:shadow-violet-500/10 transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-violet-900 dark:text-violet-100">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* CTA Section */}
      <Card className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-700 border-0">
        <CardContent className="p-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <LockKeyhole className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Secure Your Assets?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands of traders, brokers, and paymasters who trust INFRA VAULT 
              for their most valuable crypto assets.
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-violet-700 hover:bg-violet-50"
                disabled
              >
                <ExternalLink className="mr-2 h-5 w-5" />
                Access Platform (Coming Soon)
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                Learn More
              </Button>
            </div>
          </motion.div>
        </CardContent>
      </Card>

      {/* Footer Info */}
      <Card className="border-violet-200 dark:border-violet-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
                <LockKeyhole className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-violet-900 dark:text-violet-100">INFRA VAULT</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Part of INFRA TECHNOLOGY Group</p>
              </div>
            </div>
            <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
              🔒 Platform Launch: Q2 2025
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
